import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Column,
  Content,
  Grid,
  Row,
  Tile,
} from "carbon-components-react";
import { Save16, Undo16 } from "@carbon/icons-react";

import useSWR from "swr";

import { URL, Fetcher as F } from "../../api";
import * as S from "../../utils/schema";
import { SchemaEditor } from "../../components/Editor";
import { SchemaContext, UserContext } from "../../context";
import { useUserData, usePuerperaAndCenter } from "../../hooks";
import { FoodVendorRow } from "../../components/Viewer";

const EditorWrapper = styled.div`
  margin: 16px;
`;

export const FoodVendorHistoryCreateOrEdit = () => {
  const { hid, fvhid } = useParams();

  const {
    token: { access },
  } = useContext(UserContext);

  const navigate = useNavigate();
  const user = useUserData();

  const { changeFoodVendorHistory: schema } = useContext(SchemaContext) || {};

  const isEdit = !!fvhid;

  const [currentFoodVendorName, setCurrentFoodVendorName] = useState();

  const [puerpera, puerperaDisplay, center, brs] = usePuerperaAndCenter({
    housing: hid,
  });

  const currentFVURL = `${URL.currentFoodVendorOnHistory}${hid}/`;
  const { data: currentFoodVendorData } = useSWR(
    access && [currentFVURL],
    F.withToken
  );

  const historyURL = `${URL.changeFoodVendorHistory}${fvhid}/`;
  const { data: historyData } = useSWR(
    isEdit ? access && [historyURL] : null,
    F.withToken
  );

  const [isSending, setIsSending] = useState(false);

  const initialChangeFoodVendorHistory = useMemo(
    () => S.createInitialData(schema),
    [schema]
  );

  const [changeFoodVendorHistory, setChangeFoodVendorHistory] = useState(
    initialChangeFoodVendorHistory
  );

  useEffect(() => {
    if (!currentFoodVendorData || currentFoodVendorData === null) {
      return;
    }
    setCurrentFoodVendorName(currentFoodVendorData.foodvendor_name);
  }, [currentFoodVendorData]);

  useEffect(() => {
    if (!historyData) {
      return;
    }
    setChangeFoodVendorHistory(historyData);
  }, [historyData]);

  const isHistoryReady = S.createSimpleValidator(schema);
  const isReady = useMemo(
    () => isHistoryReady(changeFoodVendorHistory),
    [isHistoryReady, changeFoodVendorHistory]
  );

  const handleCancel = useCallback(async () => {
    navigate(`/housing/foodVendor/${hid}`);
  }, [navigate, hid]);

  const handleSubmit = useCallback(async () => {
    if (!isReady) return;
    if (isSending) return;

    const { id, ...o } = changeFoodVendorHistory;
    const payload = { ...o };

    setIsSending(true);
    if (isEdit) {
      await F.put(`${URL.changeFoodVendorHistory}${id}/`, payload);
    } else {
      await F.post(`${URL.changeFoodVendorHistory}`, payload);
    }
    setIsSending(false);
    navigate(`/housing/foodVendor/${hid}`);
  }, [
    navigate,
    hid,
    isReady,
    isEdit,
    isSending,
    setIsSending,
    changeFoodVendorHistory,
  ]);

  const isEditTitle = isEdit ? "編輯" : "新增";
  const storeButtonTitle = isEdit ? "儲存" : "新增";

  return (
    <Content className="foodVendorReason-createOrEdit">
      <Breadcrumb>
        <BreadcrumbItem href="/housing/">住房管理</BreadcrumbItem>
        <BreadcrumbItem href={`/housing/${hid}`}>
          {puerperaDisplay}
        </BreadcrumbItem>
        <BreadcrumbItem href="" isCurrentPage>
          膳食供廠商
        </BreadcrumbItem>
      </Breadcrumb>

      <Tile>{isEditTitle}退餐原因</Tile>

      <FoodVendorRow
        puerpera={puerpera}
        foodVendorName={currentFoodVendorName}
        center={center}
      />

      <Grid>
        <Column>
          <EditorWrapper>
            <SchemaEditor
              fields={schema.fields}
              excludes={[
                "id",
                "created",
                "modified",
                "employee_id",
                "housing",
                "bookroomschedule",
                "activate_date",
                "deactivate_date",
                "status",
              ]}
              data={changeFoodVendorHistory}
              onChange={(x) => {
                if (!user) return;
                setChangeFoodVendorHistory({
                  ...x,
                  housing: hid,
                  bookroomschedule: brs.id,
                  employee_id: user.employee_id,
                });
              }}
            />
          </EditorWrapper>
        </Column>

        <Column>
          <Grid>
            <Row>
              <Column sm={{ span: 1, offset: 1 }}>
                <Button
                  kind="secondary"
                  size="small"
                  renderIcon={Undo16}
                  onClick={handleCancel}
                >
                  取消
                </Button>
              </Column>
              <Column>
                <Button
                  disabled={!isReady}
                  kind="primary"
                  size="small"
                  renderIcon={Save16}
                  onClick={handleSubmit}
                >
                  {storeButtonTitle}
                </Button>
              </Column>
            </Row>
          </Grid>
        </Column>
      </Grid>
    </Content>
  );
};
